/**
 * In-game MUSIC — a tiny procedural synth + look-ahead step sequencer built on the SAME Web Audio
 * engine as the SFX ({@link ./feedback.ts}). NO audio assets: the track is plain data (a chord
 * progression + BPM) played through simple synth voices, so it stays tiny + offline + on-brand and
 * is easy to author. (Produced/DAW tracks can be layered in later through the same AudioGraph.)
 *
 * ADAPTIVE: `setIntensity(0..1)` brightens the pad's filter and brings the arp/lead + drive pulse
 * in/out, so the music tracks the match — calm in menus → arp during play → frantic in the final
 * stretch. Timing uses the standard Web Audio look-ahead scheduler (a 25ms timer schedules notes
 * ~100ms ahead) so it stays rock-solid regardless of frame rate.
 *
 * LIFECYCLE: `start()` builds the graph once; `setPlaying(false)` fades it out (the match is over)
 * and `setPlaying(true)` resumes it (a new match) — see `main.ts`.
 *
 * MIXING (best-practice polish): the music is its own sub-mix that sits UNDER the SFX (headroom),
 * a high-shelf carves space up top for the bright SFX (whoosh / contact click / crowd), and a
 * dedicated `duck()` node lets important SFX side-chain the music down briefly so they cut through.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * AUTHORING (musician-facing): edit BPM + PROG (the chord progression, MIDI note numbers) for the
 * tune; edit the VOICE blocks (waveform / filter / ADSR / detune / gain) for the timbres. The bass
 * + arp are derived from each chord, so the progression is the main thing you write.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 */
import type { AudioGraph } from './feedback.ts';

// --- SONG (edit me) ----------------------------------------------------------------------------
/** Tempo (beats/min). LOCKED TO THE MATCH TIMER: at 60 BPM one beat = one second, so the quarter-
 *  note pulse lands exactly on each countdown tick (the drive "kick" + the timer's final-second
 *  pop share the same cadence). Keep it a divisor of 60 (60 / 120) to stay timer-locked. */
const BPM = 60;
/** The looping chord progression — one chord per bar, as MIDI note numbers (60 = middle C). A
 *  mellow I–vi–IV–V in A major with 7ths (calm, "fairway at dusk"). notes[0] is the root. The SFX
 *  are snapped to this SAME key (A major) in feedback.ts so every sound feels composed together. */
const PROG: readonly { readonly name: string; readonly notes: readonly number[] }[] = [
  { name: 'Amaj7', notes: [57, 61, 64, 68] }, // A  C# E  G#
  { name: 'F#m7', notes: [54, 57, 61, 64] }, //  F# A  C# E
  { name: 'Dmaj7', notes: [50, 54, 57, 61] }, // D  F# A  C#
  { name: 'E7', notes: [52, 56, 59, 62] }, //    E  G# B  D
];
const STEPS_PER_BAR = 8; // eighth-note grid
const LOOP_STEPS = PROG.length * STEPS_PER_BAR;

// --- VOICES (edit me) --------------------------------------------------------------------------
interface Voice {
  readonly type: OscillatorType;
  /** Detune (cents) per stacked oscillator — multiple entries = a fatter, chorused voice. */
  readonly detune: readonly number[];
  readonly cutoff: number; // base low-pass cutoff (Hz); scaled up by intensity
  readonly q: number;
  readonly attack: number;
  readonly decay: number;
  readonly sustain: number; // 0..1 of peak
  readonly release: number;
  readonly gain: number; // peak gain per note
}
/** The sustained chord BED. Kept deliberately soft + dark (low gain, low cutoff) so it underpins
 *  the mix without dominating — it's a cushion, not a lead. */
const PAD: Voice = { type: 'sawtooth', detune: [-6, 6], cutoff: 950, q: 0.5, attack: 0.5, decay: 0.6, sustain: 0.62, release: 1.1, gain: 0.022 };
const BASS: Voice = { type: 'triangle', detune: [0], cutoff: 680, q: 0.7, attack: 0.014, decay: 0.2, sustain: 0.5, release: 0.2, gain: 0.16 };
const ARP: Voice = { type: 'square', detune: [0, 9], cutoff: 2600, q: 0.8, attack: 0.005, decay: 0.14, sustain: 0.0001, release: 0.12, gain: 0.05 };
/** A driving low sub-thump (a soft "kick") that fades IN with intensity to make the late match feel
 *  dire — a heartbeat under the bed, one per quarter-beat = one per second (timer-locked). */
const PULSE: Voice = { type: 'sine', detune: [0], cutoff: 240, q: 0.7, attack: 0.004, decay: 0.18, sustain: 0.0001, release: 0.08, gain: 0.44 };

/** Below this intensity the arp/lead layer drops out (just the calm pad + bass bed). */
const ARP_THRESHOLD = 0.45;
/** At/above this the arp doubles into 16ths (more frantic) and the drive pulse is at full force. */
const FRANTIC = 0.8;
/** The pulse begins fading in here and reaches full force by {@link FRANTIC}. */
const PULSE_IN = 0.55;
/** Music sub-mix level into the master bus — sits UNDER the SFX for headroom (gain staging). */
const MUSIC_LEVEL = 0.38;
/** Fade time (s) for start / stop / resume so the music never clicks in or cuts out. */
const FADE = 0.18;

const mtof = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export interface Music {
  /** Begin (or resume) playing through the shared audio graph (no-op if null). */
  start(graph: AudioGraph | null): void;
  /** Fully stop (page unload). Use {@link setPlaying} for match start/end. */
  stop(): void;
  /** Play (a match/menu is active) or fade out (the match is over). */
  setPlaying(on: boolean): void;
  /** Pause/resume the sequencer (the global mute also drops the master, so this just saves CPU). */
  setMuted(muted: boolean): void;
  /** 0 (calm bed) … 1 (bright + arp + tension) — drive this from match state. */
  setIntensity(level: number): void;
  /** User music volume (0..1) — scales the music sub-mix. */
  setVolume(v: number): void;
  /** Side-chain: briefly dip the music so an important SFX cuts through. depth 0..1, release secs. */
  duck(depth?: number, release?: number): void;
}

export function createMusic(): Music {
  let ctx: AudioContext | null = null;
  let bus: GainNode | null = null; // music sub-mix level (start/stop/resume fades live here)
  let duckGain: GainNode | null = null; // side-chain dip node (independent of the level fade)
  let delay: DelayNode | null = null; // an echo send for the arp (synthwave space)
  let timer: ReturnType<typeof setInterval> | null = null;
  let stepTime = 0;
  let step = 0;
  let intensity = 0.4;
  let muted = false;
  let playing = false;
  let volume = 1; // 0..1 user music volume (scales MUSIC_LEVEL), set from settings

  /** Effective music sub-mix level (preset × user volume). */
  const level = (): number => MUSIC_LEVEL * volume;

  /** Play one synth note (ADSR-enveloped, filtered, optionally chorused + echoed). `gainScale`
   *  lets a layer fade in/out with intensity without mutating the voice preset. */
  function voiceNote(v: Voice, midi: number, at: number, dur: number, sendDelay: boolean, gainScale = 1): void {
    if (!ctx || !bus) return;
    const peak = Math.max(0.0001, v.gain * gainScale);
    const sus = Math.max(0.0001, peak * v.sustain);
    const hold = Math.max(dur, v.attack + v.decay);

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(peak, at + v.attack);
    g.gain.exponentialRampToValueAtTime(sus, at + v.attack + v.decay);
    g.gain.setValueAtTime(sus, at + hold);
    g.gain.exponentialRampToValueAtTime(0.0001, at + hold + v.release);

    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = v.cutoff * (0.55 + 0.7 * intensity); // brighter as the match heats up
    filt.Q.value = v.q;

    const stopAt = at + hold + v.release + 0.05;
    for (const det of v.detune) {
      const o = ctx.createOscillator();
      o.type = v.type;
      o.frequency.value = mtof(midi);
      o.detune.value = det;
      o.connect(filt);
      o.start(at);
      o.stop(stopAt);
    }
    filt.connect(g);
    g.connect(bus);
    if (sendDelay && delay) g.connect(delay);
  }

  /** Decide + schedule the notes for one eighth-note step at audio time `at`. */
  function scheduleStep(s: number, at: number): void {
    const bar = Math.floor(s / STEPS_PER_BAR) % PROG.length;
    const chord = PROG[bar];
    if (!chord) return;
    const inBar = s % STEPS_PER_BAR;
    const eighth = 60 / BPM / 2;
    const root = chord.notes[0] ?? 57;

    // PAD: the chord, held for the whole bar (a soft sustained bed).
    if (inBar === 0) for (const n of chord.notes) voiceNote(PAD, n, at, eighth * STEPS_PER_BAR * 0.96, false);

    // BASS: the root (an octave below) on beats 1 and 3 of the bar.
    if (inBar === 0 || inBar === 4) voiceNote(BASS, root - 12, at, eighth * 3.4, false);

    // DRIVE PULSE: a sub thump on every quarter-beat (= every second at 60 BPM) that FADES IN as
    // the match gets dire — this is the "kick" the final-second timer pop is locked to.
    if (intensity >= PULSE_IN && inBar % 2 === 0) {
      const drive = Math.min(1, (intensity - PULSE_IN) / (FRANTIC - PULSE_IN));
      voiceNote(PULSE, root - 24, at, eighth * 0.6, false, drive);
    }

    // ARP/LEAD: comes in mid-build (eighth notes), then DOUBLES into 16ths when frantic.
    if (intensity >= ARP_THRESHOLD) {
      const note = chord.notes[inBar % chord.notes.length] ?? root;
      voiceNote(ARP, note + 12, at, eighth * 0.9, true);
      if (intensity >= FRANTIC) {
        const nxt = chord.notes[(inBar + 1) % chord.notes.length] ?? root;
        voiceNote(ARP, nxt + 12, at + eighth / 2, eighth * 0.45, true, 0.8); // the in-between 16th
      }
    }
  }

  /** Look-ahead scheduler: schedule every step whose time falls inside the next ~100ms. */
  function tick(): void {
    if (!ctx) return;
    const eighth = 60 / BPM / 2;
    while (stepTime < ctx.currentTime + 0.1) {
      scheduleStep(step, stepTime);
      stepTime += eighth;
      step = (step + 1) % LOOP_STEPS;
    }
  }

  function startScheduler(): void {
    if (timer !== null || !ctx) return;
    stepTime = ctx.currentTime + 0.12; // re-anchor so a resume never schedules a backlog burst
    timer = setInterval(tick, 25);
  }
  function stopScheduler(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }
  /** The sequencer should run only while started + playing + un-muted (saves CPU otherwise). */
  function syncScheduler(): void {
    if (bus !== null && playing && !muted) startScheduler();
    else stopScheduler();
  }
  /** Ramp the sub-mix level (start/stop/resume) — never a hard click. */
  function fadeBus(target: number): void {
    if (bus && ctx) bus.gain.setTargetAtTime(target, ctx.currentTime, FADE);
  }

  return {
    start(graph): void {
      if (bus === null) {
        if (!graph) return; // no audio available
        ctx = graph.ctx;
        bus = ctx.createGain();
        bus.gain.value = 0; // fade up from silence
        duckGain = ctx.createGain();
        duckGain.gain.value = 1; // 1 = open; duck() dips it toward 0
        // High-shelf cut: keep the music out of the bright band the SFX live in (EQ separation), so
        // the whoosh / contact click / crowd sparkle always read clearly over the bed.
        const shelf = ctx.createBiquadFilter();
        shelf.type = 'highshelf';
        shelf.frequency.value = 3200;
        shelf.gain.value = -5;
        bus.connect(duckGain);
        duckGain.connect(shelf);
        shelf.connect(graph.master);
        // A dotted-eighth feedback echo, sent to from the arp only, for a little synthwave space.
        delay = ctx.createDelay(1.0);
        delay.delayTime.value = (60 / BPM / 2) * 1.5;
        const fb = ctx.createGain();
        fb.gain.value = 0.3;
        delay.connect(fb);
        fb.connect(delay);
        const wet = ctx.createGain();
        wet.gain.value = 0.25;
        delay.connect(wet);
        wet.connect(bus); // echo feeds the bus, so it ducks + shelves with everything else
      }
      playing = true;
      fadeBus(level());
      syncScheduler();
    },
    stop(): void {
      playing = false;
      stopScheduler();
      fadeBus(0);
    },
    setPlaying(on): void {
      playing = on;
      fadeBus(on ? level() : 0); // the bus fade silences any ringing tails on stop
      syncScheduler();
    },
    setVolume(v): void {
      volume = clamp01(v);
      if (playing) fadeBus(level());
    },
    setMuted(m): void {
      muted = m;
      syncScheduler();
    },
    setIntensity(level): void {
      intensity = clamp01(level);
    },
    duck(depth = 0.5, release = 0.3): void {
      if (!duckGain || !ctx || !playing || muted) return;
      const now = ctx.currentTime;
      const d = clamp01(depth);
      // Snappy dip, then a SHORT linear recovery (long releases pump/swell — best-practice).
      duckGain.gain.cancelScheduledValues(now);
      duckGain.gain.setValueAtTime(Math.max(0.0001, 1 - d), now);
      duckGain.gain.linearRampToValueAtTime(1, now + Math.max(0.05, release));
    },
  };
}
